import isEqual from 'lodash/isEqual';
import { Text } from 'slate';
import type { DeepPartial } from 'ts-essentials';

import type { AlineText, ParagraphElement } from '../../slate-api';
import type { ExtractedTextSegment, SegmentStyle } from '../shared/types';

export type ParagraphTextStyles = {
  fontSize: number;
  segments: ExtractedTextSegment[];
};

function extractSegmentStyleFromTextNode(node: DeepPartial<AlineText>): SegmentStyle | undefined {
  const style: SegmentStyle = {};
  if (node.bold?.active) style.bold = true;
  if (node.italic?.active) style.italic = true;
  if (node.underline?.active) style.underline = true;
  if (node.strikethrough?.active) style.strikethrough = true;
  if (node.fontSize?.size) style.fontSize = node.fontSize.size;
  return Object.keys(style).length > 0 ? style : undefined;
}

/**
 * Find the first insertNode or deleteNode edit type from the suggested edits on
 * the text node.
 */
function getEditType(node: DeepPartial<AlineText>): ExtractedTextSegment['edit'] {
  for (const edit of node.suggestedEdits ?? []) {
    if (edit?.type === 'insertNode') return 'insert';
    if (edit?.type === 'deleteNode') return 'remove';
  }
  return 'none';
}

export function extractFromParagraphText(paragraph: ParagraphElement): ParagraphTextStyles {
  const result: ParagraphTextStyles = {
    fontSize: 12,
    segments: [],
  };
  const state = { foundFirstNonWhitespace: false, foundFirstTextNode: false };

  function processTextNode(node: DeepPartial<AlineText>) {
    const editType = getEditType(node);
    const segStyle = extractSegmentStyleFromTextNode(node);
    const lastSegment = result.segments[result.segments.length - 1];

    if (lastSegment && lastSegment.edit === editType && isEqual(lastSegment.style, segStyle)) {
      lastSegment.text += node.text;
    } else {
      result.segments.push({ edit: editType, style: segStyle, text: node.text ?? '' });
    }

    /**
     * In case there are no non-whitespace text nodes, we want to use the styles
     * from the first text node as a default.
     */
    if (!state.foundFirstTextNode) {
      state.foundFirstTextNode = true;
      result.fontSize = node.fontSize?.size ?? 12;
    }
    /**
     * We are looking for the first non-whitespace text node to use as the style
     * for the paragraph. If one is never found, it will use the style from the
     * first text node as a default.
     */
    if (!state.foundFirstNonWhitespace && node.text?.trim() !== '') {
      state.foundFirstNonWhitespace = true;
      result.fontSize = node.fontSize?.size ?? 12;
    }
  }

  for (const child of paragraph.children) {
    if (Text.isText(child)) {
      processTextNode(child);
    } else if ('children' in child) {
      /**
       * Handle inline elements with children (like links). We don't have to
       * worry about nested inline elements here as there is only one level of
       * inline elements in DOCX.
       */
      for (const inlineChild of child.children) {
        if (Text.isText(inlineChild)) {
          processTextNode(inlineChild);
        }
      }
    }
  }

  return result;
}
